import React, { useMemo } from "react";
import { Grid, Paper, Typography, Box } from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { ResizableTable } from "../../../components/resizableTable/ResizableTable";
import * as d3 from "d3";

const QUARTER_BG_COLORS = ["#4471c4", "#5b9bd5"];
const d3ThousandFormatter = d3.format(",.0f");
const d3CountFormatter = d3.format(",");

const toTitleCase = (str) =>
  (str || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Build ResizableTable column definitions for the pivot detail table
const buildDetailColumns = (quarters, UICols, type, label, timeline, currencySymbol = "") => {
  if (!quarters || !UICols || quarters.length === 0 || UICols.length === 0)
    return [];

  const acvLabel = `ACV (${currencySymbol}K)`;

  const cols = [
    {
      label: "",
      sortable: false,
      subColumns: [
        {
          label: "#",
          field: "serialNo",
          type: "number",
          align: "center",
          width: 40,
          sortable: false,
          filterable: false,
        },
      ],
      headerBgColor: "#ffffff",
      headerTextColor: "#000000",
    },
    {
      label: toTitleCase(timeline),
      sortable: true,
      subColumns: [
        {
          label: label,
          field: type,
          type: "text",
          align: "left",
          width: 150,
          sortable: true,
          filterable: true,
        },
      ],
      headerBgColor: "#ffffff",
      headerTextColor: "#000000",
    },
  ];

  quarters.forEach((q, i) => {
    cols.push({
      label: q,
      sortable: false,
      subColumns: UICols.map((uiCol) => ({
        label: uiCol.type === "currency" ? acvLabel : uiCol.label,
        field: `${uiCol.col}_${q}`,
        type: "number",
        align: "right",
        width: 70,
        sortable: true,
        filterable: true,
        headerBgColor: "#ffffff",
        headerTextColor: "#333333",
      })),
      headerBgColor: QUARTER_BG_COLORS[i % 2],
      headerTextColor: "#ffffff",
    });
  });

  if (quarters.length > 1) {
    cols.push({
      label: "Total",
      sortable: false,
      subColumns: UICols.map((uiCol) => ({
        label: uiCol.type === "currency" ? acvLabel : uiCol.label,
        field: `${uiCol.col}_Total`,
        type: "number",
        align: "right",
        width: 70,
        sortable: true,
        filterable: true,
        headerBgColor: "#ffffff",
        headerTextColor: "#333333",
      })),
      headerBgColor: QUARTER_BG_COLORS[quarters.length % 2],
      headerTextColor: "#ffffff",
    });
  }

  return cols;
};

// Transform long-format raw data into wide pivot rows for ResizableTable
const buildDetailRows = (data, type, timeline, quarters, UICols) => {
  if (
    !data ||
    data.length === 0 ||
    !type ||
    !timeline ||
    !quarters ||
    !UICols
  )
    return [];

  const uniqueAccounts = [...new Set(data.map((r) => r[type]).filter(Boolean))];

  const acvCol = UICols.find((c) => c.type === "currency")?.col;
  const pctCols = UICols.filter((c) => c.type === "percentage");
  const rawCols = UICols.filter((c) => c.type !== "percentage");
  // Reference column for % of Total computation (ACV, or fallback to first raw col)
  const pctRefCol = acvCol || rawCols[0]?.col;

  // Sort accounts by total ACV descending
  if (acvCol) {
    uniqueAccounts.sort((a, b) => {
      const aTotal = data
        .filter((r) => r[type] === a)
        .reduce((s, r) => s + (r[acvCol] || 0), 0);
      const bTotal = data
        .filter((r) => r[type] === b)
        .reduce((s, r) => s + (r[acvCol] || 0), 0);
      return bTotal - aTotal;
    });
  }

  // Pre-compute quarter totals and grand total for percentage computation
  const quarterTotals = {};
  quarters.forEach((q) => {
    quarterTotals[q] = data
      .filter((r) => r[timeline] === q)
      .reduce((s, r) => s + (r[pctRefCol] || 0), 0);
  });
  const grandTotal = data.reduce((s, r) => s + (r[pctRefCol] || 0), 0);

  const rows = uniqueAccounts.map((account) => {
    const row = { [type]: account, label: account, rowType: "data" };
    quarters.forEach((q) => {
      const cellData = data.find(
        (r) => r[timeline] === q && r[type] === account
      );
      rawCols.forEach((c) => {
        row[`${c.col}_${q}`] = cellData ? cellData[c.col] ?? null : null;
      });
      // Compute % of Total for this quarter
      if (pctRefCol) {
        const accountVal = cellData ? cellData[pctRefCol] || 0 : 0;
        const qTotal = quarterTotals[q] || 0;
        pctCols.forEach((c) => {
          row[`${c.col}_${q}`] = qTotal > 0 ? Math.round((accountVal / qTotal) * 100) : null;
        });
      }
    });
    rawCols.forEach((c) => {
      row[`${c.col}_Total`] = data
        .filter((r) => r[type] === account)
        .reduce((s, r) => s + (r[c.col] || 0), 0);
    });
    // Compute % of Total across all quarters
    if (pctRefCol) {
      const accountTotal = data
        .filter((r) => r[type] === account)
        .reduce((s, r) => s + (r[pctRefCol] || 0), 0);
      pctCols.forEach((c) => {
        row[`${c.col}_Total`] = grandTotal > 0 ? Math.round((accountTotal / grandTotal) * 100) : null;
      });
    }
    return row;
  });

  // Grand total row
  const totalRow = { [type]: "Total", label: "Total", rowType: "total" };
  quarters.forEach((q) => {
    rawCols.forEach((c) => {
      totalRow[`${c.col}_${q}`] = data
        .filter((r) => r[timeline] === q)
        .reduce((s, r) => s + (r[c.col] || 0), 0);
    });
    pctCols.forEach((c) => {
      totalRow[`${c.col}_${q}`] = 100;
    });
  });
  rawCols.forEach((c) => {
    totalRow[`${c.col}_Total`] = data.reduce((s, r) => s + (r[c.col] || 0), 0);
  });
  pctCols.forEach((c) => {
    totalRow[`${c.col}_Total`] = 100;
  });
  rows.push(totalRow);

  return rows;
};

const StrategicAccountsQualifiedPipelinePaper = ({
  classes,
  paperTitle,
  currencySymbol = "",
  strategicAccountsQualifiedPipelineData,
  createSummaryTableColumns,
  handleChangeLocation,
}) => {
  const { type, data, label, UICols, timeline, quarters, summary } =
    strategicAccountsQualifiedPipelineData || {};

  // Ensure exactly one "% of Total" column with values (remove empty duplicate from backend)
  const effectiveUICols = useMemo(() => {
    let base = UICols || [];
    // Remove "% of Total" columns that aren't type "percentage" (backend may send one we don't fill → empty duplicate)
    base = base.filter((c) => {
      const isPctLabel = (c.label || "").trim().toLowerCase() === "% of total";
      if (!isPctLabel) return true;
      return c.type === "percentage";
    });
    const hasPct = base.some((c) => c.type === "percentage");
    if (hasPct) return base;
    return [...base, { col: "pctOfTotal", label: "% of Total", type: "percentage" }];
  }, [UICols]);

  // All hooks must be called unconditionally before any early returns
  const summaryColumns = useMemo(
    () =>
      createSummaryTableColumns
        ? createSummaryTableColumns(currencySymbol)
        : [],
    [createSummaryTableColumns, currencySymbol]
  );

  const detailColumns = useMemo(
    () => buildDetailColumns(quarters, effectiveUICols, type, label, timeline, currencySymbol),
    [quarters, effectiveUICols, type, label, timeline, currencySymbol]
  );

  const detailRows = useMemo(
    () => buildDetailRows(data, type, timeline, quarters, effectiveUICols),
    [data, type, timeline, quarters, effectiveUICols]
  );

  // Loading state
  if (
    !strategicAccountsQualifiedPipelineData ||
    strategicAccountsQualifiedPipelineData === ""
  ) {
    return (
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              {paperTitle || "Strategic Accounts - Qualified Pipeline"}
            </Typography>
          </Box>
          <Skeleton
            variant="rect"
            animation="wave"
            height={400}
            width="100%"
            style={{ margin: "1rem auto" }}
          />
        </Paper>
      </Grid>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <Typography variant="h5" className={classes.title}>
              {paperTitle || "Strategic Accounts - Qualified Pipeline"}
            </Typography>
            <span className={classes.updateFrequency}>{"Live Data"}</span>
          </Box>
          <Typography
            variant="body1"
            align="center"
            style={{ padding: "2rem", color: "#666" }}
          >
            No strategic account qualified pipeline data available.
          </Typography>
        </Paper>
      </Grid>
    );
  }

  const getDetailCellValue = (row, field, rowIndex) => {
    if (field === "serialNo") return row.rowType === "total" ? "" : String(rowIndex + 1);
    if (field === type) return row[field] ?? "";
    const val = row[field];
    if (val == null) return "-";
    const matchedUICol = (effectiveUICols || []).find((c) =>
      field.startsWith(c.col + "_")
    );
    if (matchedUICol?.type === "currency") {
      return d3ThousandFormatter(val / 1000);
    }
    if (matchedUICol?.type === "percentage") {
      return `${Math.round(val)}%`;
    }
    return d3CountFormatter(val);
  };

  const getDetailCellStyle = (row, field) => {
    const style = {};
    if (row.rowType === "total") {
      style.fontWeight = 600;
      style.backgroundColor = "#f5f5f5";
    }
    return style;
  };

  const handleDetailRowClick = (row, rowIndex, columnInfo) => {
    if (!handleChangeLocation) return;
    // Skip drilldown on Total row if desired (parent supports it; optional)
    const rowValue = row[type] ?? row.label;
    const cellValue = columnInfo?.field != null
      ? (columnInfo.field.endsWith("_Total") ? "Total" : columnInfo.field.replace(/^[^_]+_/, ""))
      : "Total";
    handleChangeLocation(
      "drilldownOnGraph",
      { row: rowValue, cell: cellValue },
      type,
      timeline
    );
  };

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <Typography variant="h5" className={classes.title}>
            {paperTitle || "Strategic Accounts - Qualified Pipeline"}
          </Typography>
          <span className={classes.updateFrequency}>{"Live Data"}</span>
        </Box>

        {/* Summary Table */}
        {createSummaryTableColumns && summary && summary.length > 0 && (
          <Box mb={3}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Summary
            </Typography>
            <ResizableTable
              columns={summaryColumns}
              rows={summary}
              getCellValue={(row, field) => {
                if (field === "label") return row[field];
                return row[field] != null ? row[field] : "";
              }}
              getCellStyle={(row, field) => {
                const style = {};
                if (field === "label") style.fontWeight = 600;
                if (row.rowType === "total") {
                  style.backgroundColor = "#f5f5f5";
                  if (field === "count" || field === "acvInMillions")
                    style.fontWeight = 600;
                }
                return style;
              }}
              rowKey="label"
              hideSubHeaderRow={true}
              maxWidth="34%"
            />
          </Box>
        )}

        {/* Detail Table — Pipeline Mix by Account Name */}
        <Box mt={3}>
          <Typography
            variant="subtitle1"
            style={{ fontWeight: 600, marginTop: "4.3rem" }}
          >
            Strategic Accounts - Qualified Pipeline Detail
          </Typography>
          {detailColumns.length > 0 && detailRows.length > 0 && (
            <ResizableTable
              columns={detailColumns}
              rows={detailRows}
              getCellValue={getDetailCellValue}
              getCellStyle={getDetailCellStyle}
              rowKey="label"
              hideSubHeaderRow={false}
              maxWidth="100%"
              onRowClick={handleChangeLocation ? handleDetailRowClick : undefined}
              dataTestId="strategic-accounts-qualified-pipeline-detail-table"
            />
          )}
        </Box>
      </Paper>
    </Grid>
  );
};

export default StrategicAccountsQualifiedPipelinePaper;
